// Per-dog "mode" picker — which sections of the app apply to this dog.
import { Home, Trophy, Dumbbell, Heart, Baby, PawPrint } from "lucide-react";

export const DOG_TYPES = [
  {
    value: "puppy",
    label: "Puppy",
    icon: Baby,
    blurb: "Socialization, potty + leash training, short sessions, growth tracking.",
  },
  {
    value: "pet",
    label: "Pet parent",
    icon: Home,
    blurb: "Daily care, vet records, weight, food, journaling.",
  },
  {
    value: "show",
    label: "Show dog",
    icon: Trophy,
    blurb: "Everything: grooming prompts, show plans & results, pedigree, academy.",
  },
  {
    value: "sport",
    label: "Sport dog",
    icon: Dumbbell,
    blurb: "Training, gait, exercise prompts, event results, education.",
  },
  {
    value: "breeder",
    label: "Breeder",
    icon: PawPrint,
    blurb: "Heat cycles, gestation, litters, puppy tracking, ENS, x-rays, stud records.",
  },
  {
    value: "senior",
    label: "Senior dog",
    icon: Heart,
    blurb: "Gentle, regular movement. All tracking sections available.",
  },
];

// Routes hidden per mode. Anything not listed stays visible.
const HIDDEN = {
  puppy: new Set(["/show-plans", "/show-results"]),
  pet: new Set(["/show-plans", "/show-results", "/academy"]),
  show: new Set(),
  sport: new Set(["/show-plans"]),
  breeder: new Set(),
  senior: new Set(),
};

export function isRouteVisible(dogType, routePath) {
  const t = dogType || "show";
  const set = HIDDEN[t] || HIDDEN.show;
  return !set.has(routePath);
}

export function getDogTypeMeta(value) {
  return DOG_TYPES.find((t) => t.value === value) || DOG_TYPES[1];
}

// Per-mode prompt config used by the Overview page.
// Each prompt has a stable `key` (persisted in the backend) + display label.
const PROMPTS = {
  puppy: {
    daily: [
      { key: "potty_break", label: "Potty break (aim every 2–3 hrs)" },
      { key: "potty_training", label: "Potty training session" },
      { key: "socialization", label: "Socialization (new sight / sound / person)" },
    ],
    weekly: [
      { key: "leash_training", label: "Leash training" },
      { key: "handling", label: "Handling practice (paws / ears / mouth)" },
      { key: "short_play", label: "Short structured play" },
      { key: "exercise", label: "Free play / exercise" },
    ],
  },
  pet: {
    daily: [
      { key: "walk", label: "Walk" },
      { key: "obedience", label: "Obedience training" },
      { key: "enrichment", label: "Mental enrichment / play" },
    ],
    weekly: [
      { key: "brush_teeth", label: "Brush teeth" },
      { key: "maintenance_bath", label: "Maintenance Bath 🧴" },
      { key: "nails", label: "Nails 💅" },
      { key: "clean_feet", label: "Clean Feet 🐾" },
      { key: "sanitary", label: "Sanitary ✂️" },
      { key: "check_ears", label: "Check / Clean Ears" },
      { key: "maintenance_trim", label: "Maintenance Trim ✂️" },
    ],
    monthly: [
      { key: "full_bath", label: "Full Bath 🛁" },
      { key: "full_groom", label: "Full Groom ✂️" },
    ],
  },
  show: {
    daily: [
      { key: "stacking", label: "Stacking 🧍" },
      { key: "line_brush", label: "Line Brush / Comb 🪮" },
      { key: "dematt", label: "Dematt problem areas 🪮" },
    ],
    weekly: [
      { key: "maintenance_bath", label: "Maintenance Bath 🧴" },
      { key: "nails", label: "Nails 💅" },
      { key: "clean_feet", label: "Clean Feet 🐾" },
      { key: "sanitary", label: "Sanitary ✂️" },
      { key: "gaiting", label: "Gaiting 🐕" },
      { key: "cavaletti", label: "Cavaletti 🚧" },
      { key: "scissor_work", label: "Scissor Work ✂️" },
      { key: "clipper_work", label: "Clipper Work 💈" },
      { key: "strength_balance_proprio", label: "Strength / Balance / Proprioception 🏋️" },
      { key: "maintenance_trim", label: "Maintenance Trim ✂️" },
    ],
    monthly: [
      { key: "full_bath", label: "Full Bath 🛁" },
      { key: "full_groom", label: "Full Groom ✂️" },
    ],
  },
  sport: {
    daily: [
      { key: "warmup_walk", label: "Walk" },
      { key: "stretch_routine", label: "Stretch routine" },
    ],
    weekly: [
      { key: "cavaletti", label: "Cavaletti 🚧" },
      { key: "strength_training", label: "Strength training" },
      { key: "hill_sprint", label: "Treadmill / Trot Work" },
      { key: "stamina", label: "Stamina / endurance" },
      { key: "nails", label: "Nails 💅" },
      { key: "check_ears", label: "Check ears" },
      { key: "brush_teeth", label: "Brush teeth" },
    ],
    monthly: [
      { key: "full_bath", label: "Full Bath 🛁" },
      { key: "full_groom", label: "Full Groom ✂️" },
    ],
  },
  senior: {
    daily: [
      { key: "slow_walk", label: "Slow walk" },
      { key: "comfort_check", label: "Comfort check" },
      { key: "regular_movement", label: "Regular movement" },
    ],
    weekly: [
      { key: "low_impact_strength", label: "Low-impact strength" },
      { key: "gentle_stretch", label: "Gentle stretch" },
      { key: "sanitary", label: "Sanitary ✂️" },
      { key: "nails", label: "Nails 💅" },
      { key: "soft_brushing", label: "Soft brushing" },
      { key: "check_ears", label: "Check ears" },
      { key: "brush_teeth", label: "Brush teeth" },
    ],
    monthly: [
      { key: "full_bath", label: "Full Bath 🛁" },
      { key: "mobility_assessment", label: "Mobility / pain assessment" },
    ],
  },
  // Breeder-focused dam/stud journey — daily care plus breeding-relevant
  // checks (body condition, weight, heat-cycle awareness). Routes (heat
  // cycles, gestation, litters) live in the Breeding sidebar group and are
  // visible to all users; these prompts surface on the dog's Overview page.
  breeder: {
    daily: [
      { key: "walk", label: "Walk" },
      { key: "body_condition_check", label: "Body condition check" },
      { key: "obedience", label: "Obedience training" },
    ],
    weekly: [
      { key: "weight_check", label: "Weight check ⚖️" },
      { key: "heat_cycle_check", label: "Heat-cycle check 🌸" },
      { key: "supplement_check", label: "Supplement / fertility check 💊" },
      { key: "brush_teeth", label: "Brush teeth" },
      { key: "nails", label: "Nails 💅" },
      { key: "sanitary", label: "Sanitary ✂️" },
      { key: "maintenance_trim", label: "Maintenance Trim ✂️" },
    ],
    monthly: [
      { key: "full_bath", label: "Full Bath 🛁" },
      { key: "vet_screen", label: "Vet / health screen 🩺" },
    ],
  },
};

export function getPromptsConfig(dogType) {
  const cfg = PROMPTS[dogType || "show"] || PROMPTS.show;
  return { daily: cfg.daily || [], weekly: cfg.weekly || [], monthly: cfg.monthly || [] };
}

// All dogs benefit from a 45-minute warm-up walk before structured exercise.
export function showsWarmupReminder(_dogType) {
  return true;
}

// Senior dogs need a special "gentle but regular" exercise reminder.
export function showsSeniorReminder(dogType) {
  return (dogType || "") === "senior";
}

// Puppies need short sessions + growth-appropriate exercise reminders.
export function showsPuppyReminder(dogType) {
  return (dogType || "") === "puppy";
}
